import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../../../core/services/core.user.service';
import { AssociationRest } from '../../../../core/services/core.association.rest';
import { UiService } from '../../../../core/services/core.ui.service';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../../../core/services/core.translate.service';
import { AssociationAddModalComponent } from './associationAddModal/association.add.modal.component';
import * as _ from 'lodash';

@Component({
  selector: 'gf-associations',
  template: require('./associations.component.html')
})
export class AssociationsComponent implements OnInit {
  @Input() project: any;
  @Input() section: string;
  @Input() refId: string;
  @Input() allowUpdates: boolean;

  // Emitted whenever fetchAssociations() resolves, so a parent (e.g. RelatedTabComponent) can
  // know the association count -- this component fetches independently of the parent's own
  // trackeritem data, so there's no other way for the parent to find out.
  @Output() loaded: EventEmitter<any[]> = new EventEmitter<any[]>();

  associations: any[] = [];
  addAssociation: any = {
    searchItemsResult: [],
    search: '',
    autoSubmitTimer: null
  };
  markedForDeletion: number = 0;
  loading: boolean = false;

  constructor(
    private userService: UserService,
    private associationRest: AssociationRest,
    private uiService: UiService,
    private translateService: GfTranslateService,
    private http: HttpClient,
    private modalService: NgbModal,
    private stateService: StateService, 
  ) {}

  ngOnInit(): void {
    this.fetchAssociations();
  }

  addAssociatedItem(item: any, comment: string = ''): void {
    this.addAssociation.search = '';
    this.addAssociation.searchItemsResult = [];

    const params = {
      section: this.section,
      refId: this.refId,
      toSection: this.mapItemSection(item.section),
      toRefId: item.refId,
      comment: comment
    };

    this.associationRest.save(params)
      .then(() => {
        this.fetchAssociations();
      }, (err) => {
        this.uiService.showErrorMsg(this.translateService.translate("There was an error trying to add the tracker item association"));
      });
  }

  addAssociationForAutoSubmit(searchValue: string): void {
    if (this.addAssociation.autoSubmitTimer) {
      clearTimeout(this.addAssociation.autoSubmitTimer);
    }
    this.addAssociation.autoSubmitTimer = setTimeout(() => {
      this.addAssociationForSearchItems(searchValue);
    }, 750);
  }

  addAssociationForSearchItems(searchValue: string): void {
    this.addAssociation.searchItemsResult = [];

    if (searchValue.length < 3) {
      return;
    }

    this.addAssociation.searching = true;

    const params = new HttpParams()
    .set('q', searchValue)
    .set('s', 'trackeritem,wiki,docman,scm,forum')
    .set('project', this.project.id)
    .set('page_num', "1")
    .set('page_size', "20");

    this.http.get('/api/search/', {params: params})
      .subscribe((response: any) => {
        this.addAssociation.searchItemsResult = response.items.sort((a: any, b: any) => b.matchScore - a.matchScore).map((item: any) => {
          item.readableSection = this.getReadableSection(item.section);
          return item;
        });
        this.addAssociation.searching = false;
      }, (err) => {
        const errorMsg = this.translateService.translate('Sorry, {{searchValue}} does not exists or you ' +
          'are not allowed to view it', {'searchValue' : searchValue});
        const errorMsgTitle = this.translateService.translate('Invalid Tracker Item');
        this.uiService.showWarningMsg(errorMsg, errorMsgTitle);
        this.addAssociation.searching = false;
        this.addAssociation.search = '';
      });
  }

  removeAssociation(item: any): void {
    if (this.markedForDeletion != item.id) {
      this.markedForDeletion = item.id;
      return;
    }

    this.associationRest.delete(this.section, this.refId, item.section, item.id)
      .then(() => {
        this.associations = this.associations.filter((association) => association.id !== item.id && association.section !== item.section);
        this.uiService.showSuccessMsg(this.translateService.translate("The association was deleted"));
      }, (err) => {
        this.uiService.showErrorMsg(this.translateService.translate("There was an error trying to delete the association"));
      })
      .then(() => {
        this.markedForDeletion = 0;
      });
  }

  goTo(item: any): void {
    let stateName: string = '';
    let params: any = {};

    if (item.section == 'forummessage') {
      this.http.get('/api/forummessage/' + item.id + '/location')
        .subscribe((data: any) => {
          const _params = {
            unixName: data.refId,
            id: data.forum,
            thread: data.forumThread
          };
          this.stateService.go('home.project.summary.forum.read', _params);
        }, () => {});
    } else {
      switch(item.section) {
        case 'trackeritem':
          stateName = 'home.project.summary.tracker.item.edit';
          params = { unixName: item.tracker.project.unixName, tracker:  item.tracker.id, id: item.id };
          break;
  
        case 'docmanfolder':
          stateName = 'home.project.summary.docman';
          params =  {unixName: item.project.unixName, folder: item.id};
          break;

        case 'docmanfile':
          stateName = 'home.project.summary.docman';
          params = {unixName: item.folders[0].project.unixName, folder: item.folders[0].name};
          break;

        case 'wikipage':
          stateName = 'home.project.summary.wiki.page';
          params = { unixName: item.refId, pagename: item.pagename };
          break;
      }

      this.stateService.go(stateName, params);
    }
  }

  showAssociationAddModal(associatedItem: any): void {
    const modalRef = this.modalService.open(AssociationAddModalComponent);
    modalRef.componentInstance.item = associatedItem;
    modalRef.componentInstance.save = this.addAssociatedItem.bind(this);
	modalRef.componentInstance.close = () => {
		modalRef.close();
	};
  }

  getReadableSection(section: string): string {
    switch(section) {
      case 'trackeritem':
        return this.translateService.translate('Tracker Item');
      case 'docmanfolder':
        return this.translateService.translate('Docman Folder');
      case 'docmanfile':
        return this.translateService.translate('Docman File');
      case 'wikipage':
        return this.translateService.translate('Wiki Page');
      case 'forummessage':
        return this.translateService.translate('Forum Message');
      default:
        return section;
    }
  }

  mapItemSection(section: string = ''): string {
    switch(section) {
      case 'wiki':
        return 'wikipage';
      case 'docman':
        return 'docmanfile';
      default:
        return section;
    }
  }

  fetchAssociations(): void {
    if (!this.userService.hasUser()) return;

    this.loading = true;
    this.associationRest.fetch(this.section, this.refId)
      .then((ret: any) => {
        this.associations = [];
        _.forEach(_.get(ret, 'items', []), (sectionItems, section) => {
          _.forEach(sectionItems, (item) => {
            item.item.section = section;
            item.item.readableSection = this.getReadableSection(section);
            item.item.comment = item.comment;
            this.associations.push(item.item);
          })
        });
        this.loaded.emit(this.associations);
      }, () => {})
	  .then(() => {
        this.loading = false;
      });
  }

  isMarkedForDeletion(item): any {
    return this.markedForDeletion === item.id ? { color: 'red' } : {};
  }
}
